import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { reduxForm } from 'redux-form';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import classnames from 'classnames';
import range from 'lodash/range';

import ORTypography from 'components/ORTypography/ORTypography';

const propTypes = {
  addonId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  description: PropTypes.string,
  addonValue: PropTypes.shape({type: PropTypes.Any }).isRequired,
  className: PropTypes.string,
  change: PropTypes.func.isRequired,
  trackButtonClick: PropTypes.func.isRequired,
  selectedService: PropTypes.string.isRequired,
};

const defaultProps = {
  description: '',
  className: '',
};

const useStyles = makeStyles((theme) => ({
  tooltip: {
    maxWidth: 125,
  },
  addonSelector: {
    border: `2px solid ${theme.customColors.mediumGrey}`,
    backgroundColor: (props) =>
      props.selected ? `${theme.customColors.teal}` : `${theme.customColors.white}`,
    color: (props) =>
      props.selected ? `${theme.customColors.white}` : `${theme.customColors.black}`,
    position: 'relative',
    cursor: 'pointer',
    padding: theme.spacing(1),
  },
  addonName: {
    fontWeight: 500,
    fontSize: '1rem',
  },
  addonDescription: {
    fontSize: '1rem',
  },
  valueMenuButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 0,
    color: (props) =>
      props.selected ? `${theme.customColors.white}` : `${theme.customColors.black}`,
  },
  valueMenuButtonLabel: {
    fontFamily: ['"Libre Franklin"', 'sans-serif'],
    fontSize: '.857rem',
    color: (props) =>
      props.selected ? `${theme.customColors.white}` : `${theme.customColors.black}`,
  },
}));

function getPossibleValues(valueObject) {
  const { type } = valueObject;
  if (type === 'integer') {
    return range(Number(valueObject.minimum), Number(valueObject.maximum) + 1);
  }
  if (type === 'string') {
    return [...valueObject.enum];
  }
  return [];
}

const REQUIRED_MONGODB_ADDONS = ['shards', 'mongos', 'mongos_plan', 'configsvr_plan'];
const REQUIRED_MONGODB_ADDONS_NOT_SELECTED = ['per_mongos'];

export function UnstyledAddonSelector({
  className,
  addonId,
  name,
  displayName,
  description,
  addonValue,
  change,
  trackButtonClick,
  selectedService,
}) {
  const [isSelected, toggleIsSelected] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentAddonDisplayIndex, setCurrentAddonDisplayIndex] = React.useState(0);

  const classes = useStyles({ selected: isSelected });
  const addonValueArray = getPossibleValues(addonValue);
  const hasAddonValues = addonValueArray.length > 0;

  // On initial mount, if the addon is required, select it by default.
  // Really it seems like there should be a "required" attribute in the API or something.
  // Note that mongo addons will ONLY be mounted if the service is mongodb and sharded is selected.
  React.useEffect(() => {
    if (REQUIRED_MONGODB_ADDONS.includes(name)) {
      toggleIsSelected(true);
      change('addons', (values) => {
        return {
          ...values,
          [addonId]: {
            value: addonValueArray[currentAddonDisplayIndex],
            name,
            displayName,
          },
        };
      });
    } else if (REQUIRED_MONGODB_ADDONS_NOT_SELECTED.includes(name)) {
      toggleIsSelected(false);
      change('addons', (values) => {
        let selectedValue = addonValueArray[currentAddonDisplayIndex];
        if (addonValue.type === "boolean") {
          selectedValue = false
        }
        return {
          ...values,
          [addonId]: {
            value: selectedValue,
            name,
            displayName,
          },
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = () => {
    // Prevent a user from deselecting a required addon.
    if (REQUIRED_MONGODB_ADDONS.includes(name)) {
      return;
    }
    if (isSelected) {
      toggleIsSelected(false);
      change('addons', (values) => {
        const retObj = { ...values };

        // While deselecting the boolean addons, we only to change their value to false.
        if (!REQUIRED_MONGODB_ADDONS_NOT_SELECTED.includes(name) && addonValue.type !== "boolean") {
          delete retObj[addonId];
        } else {
          retObj[addonId].value = false
        }
        // If there are no addons selected, this will remove the
        // addons entry completely from the redux-form store
        if (Object.keys(retObj).length === 0) {
          return undefined;
        }
        return retObj;
      });
    } else {
      toggleIsSelected(true);
      let selectedValue = addonValueArray[currentAddonDisplayIndex];
      if (addonValue.type === "boolean") {
        selectedValue = true
      }

      change('addons', (values) => {
        return {
          ...values,
          [addonId]: {
            value: selectedValue,
            name,
            displayName,
          },
        };
      });
    }
  };

  const handleAddonMenuClick = (event) => {
    if (isSelected) {
      event.stopPropagation();
    }
    setAnchorEl(event.currentTarget);
  };

  const handleAddonMenuItemClick = (event) => {
    event.stopPropagation();
    change('addons', (values) => {
      return {
        ...values,
        [addonId]: {
          value: event.target.textContent,
          name,
          displayName,
        },
      };
    });
    setCurrentAddonDisplayIndex(Number(event.target.getAttribute('index')));
    setAnchorEl(null);
  };

  /* istanbul ignore next */
  const handleVersionMenuClose = (event) => {
    if (isSelected) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const addonMenuItems = addonValueArray.map((value, index) => (
    <MenuItem
      key={value}
      index={index}
      onClick={(e) => {
        trackButtonClick(selectedService.concat(' ', value.toString(), ' Add On Menu Item'));
        handleAddonMenuItemClick(e);
      }}
    >
      {value}
    </MenuItem>
  ));

  const actionIcon = (
    <IconButton
      className={classes.valueMenuButton}
      classes={{ label: classes.valueMenuButtonLabel }}
      onClick={handleAddonMenuClick}
    >
      {isSelected ? `${addonValueArray[currentAddonDisplayIndex] || ''}` : null}
      <ExpandMoreIcon />
    </IconButton>
  );

  return (
    <Paper
      className={classnames(classes.addonSelector, className)}
      onClick={handleClick}
      elevation={0}
    >
      <ORTypography className={classes.addonName}>{displayName}</ORTypography>
      <ORTypography className={classes.addonDescription}>{description}</ORTypography>
      {hasAddonValues && actionIcon}
      <Menu
        keepMounted
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={Boolean(anchorEl)}
        onClose={handleVersionMenuClose}
      >
        {addonMenuItems}
      </Menu>
    </Paper>
  );
}

UnstyledAddonSelector.propTypes = propTypes;
UnstyledAddonSelector.defaultProps = defaultProps;

export default reduxForm({
  form: 'create',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(UnstyledAddonSelector);
